import { Component, Event, EventEmitter, h, Listen, Prop, State } from '@stencil/core';
import store from '../../../helpers/store';
import { FlowStatus } from '../../../models/FlowStatus';
import { FlowSteps } from '../../../models/FlowSteps';
import { base64Size, blobToBase64, delay, getLogMessage } from '../../../utils/utils';
import { BaseComponent } from '../../base-component';
import { CaptureSide, CaptureUploadTypes } from '../../../models/CaptureFlow';
import { ApiCall } from '../../../helpers/ApiCall';
import AppInsightsService from '../../../helpers/AppInsightsService';
import { Cameras } from '../../../helpers/Cameras';
import { LoggerService } from '../../../helpers/LoggerService';

@Component({
  tag: 'user-liveness',
  styleUrl: 'user-liveness.css',
  shadow: false,
})
export class UserLiveness {
  @Prop() public mode: string;
  @State() showError: boolean;
  @State() showTimeout: boolean;
  @State() private flow: CaptureSide;
  @State() private uploadingPhoto: boolean = false;
  @State() private uploadingRecording: boolean = false;

  private recordingResultCount: number = 0;
  private recordingRetryCount: number = 0;
  private currentStep: FlowSteps;
  private errorReason: string;
  private verificationReceived: number;

  @Event({
    eventName: 'apiError',
  })
  apiErrorEvent: EventEmitter<any>;

  private appInsights = AppInsightsService.getInstance();

  constructor() {}

  async componentDidLoad() {
    await BaseComponent.initialize(this.currentStep);
  }

  componentWillLoad() {
    this.appInsights.trackEvent('UserLiveness | init', { requestId: store.requestId });
    if (this.mode == 'selfie') {
      this.currentStep = FlowSteps.Selfie;
      this.flow = {
        fileName: 'selfie.png',
        recName: 'selfieVideo.',
        photoType: CaptureUploadTypes.Selfie,
        recType: CaptureUploadTypes.SelfieVideo,
        imageSize: 0,
        videoSize: 0,
      };
    } else if (this.mode == 'selfieGesture') {
      this.currentStep = FlowSteps.SelfieTilt;
      this.flow = {
        fileName: '',
        recName: 'selfieTiltVideo.',
        photoType: CaptureUploadTypes.None,
        recType: CaptureUploadTypes.SelfieGestureVideo,
        imageSize: 0,
        videoSize: 0,
      };
    }
  }

  @Listen('timeElapsed')
  timeElapsed() {
    this.errorReason = 'Timeout';
    this.showTimeout = true;
  }

  @Listen('captureErrorDone')
  captureErrorDone() {
    if (this.showTimeout == true) {
      this.showTimeout = false;
    }
    if (this.showError == true) {
      this.showError = false;
    }
  }

  @Listen('photoSelfieCapture')
  async captureSelfieImage(event: any) {
    if (store.flowStatus == FlowStatus.LIVENESSGESTURE) return;

    let selfiePhoto = event.detail;
    if (selfiePhoto.size == 0) {
      await LoggerService.logMulti({ message: 'Empty selfie', blobData: selfiePhoto }, await getLogMessage());
      this.triggerErrorFlow();
      return;
    }

    try {
      var uploadPhoto = new File([selfiePhoto], this.flow.fileName, { type: 'image/png' });
      let b64Data = await blobToBase64(uploadPhoto);
      this.flow.imageSize = base64Size(b64Data);
      this.uploadingPhoto = true;
      let photoUploaded = await ApiCall.instance.UploadFileForRequestB64(store.requestId, this.flow.photoType, b64Data);
      if (photoUploaded.isValid) {
        store.flowStatus = FlowStatus[photoUploaded.state];
      } else {
        this.triggerErrorFlow();
      }
      this.uploadingPhoto = false;
    } catch (e) {
      this.apiErrorEvent.emit(e);
    }
  }

  @Listen('recordingSelfieCapture')
  async capturedSelfieRecording(event) {
    if (!store.videoRecording) {
      this.uploadingRecording = true;
      return;
    }

    try {
      let selfieRecording: Blob = event.detail;
      if (selfieRecording.size == 0 && this.recordingResultCount == 0) {
        this.recordingResultCount++;
        return;
      }
      this.recordingResultCount = 0;
      if (selfieRecording.size == 0) {
        await LoggerService.logMulti({ message: 'Empty recording', blobData: selfieRecording }, await getLogMessage());
        this.triggerErrorFlow();
        return;
      }
      let mimeType = selfieRecording.type == Cameras.mp4MimeType.type ? Cameras.mp4MimeType : Cameras.webmMimeType;

      this.uploadingRecording = true;
      let uploadRec = new File([selfieRecording], this.flow.recName + mimeType.extension, { type: mimeType.type });
      let b64Data = await blobToBase64(uploadRec);
      this.flow.videoSize = base64Size(b64Data);
      let uplodDone = await ApiCall.instance.UploadFileForRequestB64(store.requestId, this.flow.recType, b64Data);
      if (uplodDone.saved) {
        store.flowStatus = FlowStatus[uplodDone.state];
      } else {
        this.uploadingRecording = false;
        this.triggerErrorFlow();
      }
    } catch (e) {
      this.uploadingRecording = false;
      if (this.recordingRetryCount < 3) {
        this.recordingRetryCount++;
        this.triggerErrorFlow();
      } else {
        this.apiErrorEvent.emit(e);
      }
    }
  }

  @Listen('verificationFinished')
  async verificationFinished() {
    LoggerService.log('user-liveness | verificationFinished');

    this.verificationReceived = Date.now();
    while (Date.now() - this.verificationReceived < 90000) {
      await delay(2000);
      if (this.showError || this.showTimeout) return;
    }
    this.triggerUploadFailedFlow();

    LoggerService.log('user-liveness | verificationFinished | waitingFinished ');
  }

  private triggerUploadFailedFlow() {
    this.errorReason = 'Upload Failed';
    this.showError = true;
  }

  async disconnectedCallback() {
    await BaseComponent.finalize(this.currentStep);
  }

  private triggerErrorFlow() {
    this.errorReason = 'Invalid';
    this.showError = true;
  }

  render() {
    let capture = <selfie-auto-capture></selfie-auto-capture>;
    let error = <capture-error type="LIVENESS" reason={this.errorReason} error={`image: ${this.flow.imageSize}mb video: ${this.flow.videoSize}mb`}></capture-error>;
    let upload = <uploading-data type="id"></uploading-data>;

    if (this.uploadingPhoto && this.uploadingRecording) return upload;

    if (this.showError || this.showTimeout) return error;

    return capture;
  }
}
